import type { ThreadType } from '@kanbu/schema';
import { Currency, ThreadCategory, ThreadMode } from '@kanbu/schema/enums';
import { formatUtils, DateFormat } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import * as Tabs from '@radix-ui/react-tabs';
import { Badge, Button, cn } from '@utima/ui';
import { MessageCircle, PhoneCall, X } from 'lucide-react';

import { DisplayValue } from '@/components/displayValue/DisplayValue';
import { CallLogs, ThreadContext, ThreadCosts } from '@/features/threads';
import { CallRecordings } from '@/features/threads/CallRecordings';
import { useEnumTranslations } from '@/hooks';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';

export interface ThreadDetailsProps {
  thread: ThreadType;
  className?: string;
  onClose: () => void;
}

export function ThreadDetails({
  thread,
  className,
  onClose,
}: ThreadDetailsProps) {
  const { t } = useLingui();
  const { threadCategory } = useEnumTranslations();
  const { organization } = useCurrentOrganization();

  const organizationCurrency = organization?.currency ?? Currency.USD;

  return (
    <div
      className={cn(
        'w-[500px] md:block hidden border-l border-gray-200 bg-white p-4 h-full overflow-auto',
        className,
      )}
    >
      <div className='mb-2 flex items-center justify-between'>
        <h3 className='text-lg font-semibold'>
          <Trans>Thread Information</Trans>
        </h3>
        <Button
          data-cta='admin-inbox-thread-details-close-button'
          variant='ghost'
          size='icon-sm'
          className='size-8 p-0'
          onClick={onClose}
        >
          <X className='size-4' />
        </Button>
      </div>

      {thread.category === ThreadCategory.Voice && (
        <div className='mb-4'>
          <CallRecordings key={thread.id} threadId={thread.id} />
        </div>
      )}

      <Tabs.Root
        className='flex min-h-0 flex-1 flex-col'
        defaultValue='metadata'
      >
        <Tabs.List className='mb-4 border-b border-border'>
          <Tabs.Trigger
            value='metadata'
            className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
          >
            <Trans>Metadata</Trans>
          </Tabs.Trigger>

          {thread.category === ThreadCategory.Chat && (
            <Tabs.Trigger
              value='context'
              className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
            >
              <Trans>Context</Trans>
            </Tabs.Trigger>
          )}

          <Tabs.Trigger
            value='logs'
            className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
          >
            <Trans>Logs</Trans>
          </Tabs.Trigger>

          <Tabs.Trigger
            value='costs'
            className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
          >
            <Trans>Costs</Trans>
          </Tabs.Trigger>
        </Tabs.List>

        <div className='min-h-0 flex-1'>
          <Tabs.Content value='metadata' className='h-full'>
            <div className='space-y-4'>
              <DisplayValue label={t`Thread ID`} className='text-sm'>
                {thread.id}
              </DisplayValue>

              <DisplayValue label={t`Category`} className='text-sm'>
                <Badge
                  className='inline-flex items-center gap-2'
                  variant={
                    thread.category === ThreadCategory.Voice
                      ? 'success'
                      : 'primary'
                  }
                >
                  {thread.category === ThreadCategory.Voice ? (
                    <PhoneCall className='size-3.5' />
                  ) : (
                    <MessageCircle className='size-3.5' />
                  )}{' '}
                  {threadCategory[thread.category]}
                </Badge>
              </DisplayValue>

              <DisplayValue label={t`Created`} className='text-sm'>
                {formatUtils.date(
                  thread.createdAt,
                  DateFormat.DateTimeNumericSeconds,
                )}
              </DisplayValue>

              <DisplayValue label={t`Updated`} className='text-sm'>
                {formatUtils.date(
                  thread.updatedAt,
                  DateFormat.DateTimeNumericSeconds,
                )}
              </DisplayValue>

              <DisplayValue label={t`Last user message`} className='text-sm'>
                {thread.lastUserMessageAt
                  ? formatUtils.date(
                      thread.lastUserMessageAt,
                      DateFormat.DateTimeNumericSeconds,
                    )
                  : '-'}
              </DisplayValue>

              <DisplayValue label={t`Last active`} className='text-sm'>
                {thread.lastActiveAt
                  ? formatUtils.date(
                      thread.lastActiveAt,
                      DateFormat.DateTimeNumericSeconds,
                    )
                  : '-'}
              </DisplayValue>

              {thread.category === ThreadCategory.Voice && (
                <>
                  <DisplayValue label={t`Phone number`} className='text-sm'>
                    {thread.chatUser?.phone || '-'}
                  </DisplayValue>

                  <DisplayValue label={t`Call started`} className='text-sm'>
                    {thread.callStartedAt
                      ? formatUtils.date(
                          thread.callStartedAt,
                          DateFormat.DateTimeNumericSeconds,
                        )
                      : '-'}
                  </DisplayValue>

                  <DisplayValue label={t`Call ended`} className='text-sm'>
                    {thread.callEndedAt
                      ? formatUtils.date(
                          thread.callEndedAt,
                          DateFormat.DateTimeNumericSeconds,
                        )
                      : '-'}
                  </DisplayValue>

                  {thread.callEndedAt && thread.callStartedAt && (
                    <DisplayValue label={t`Call length`} className='text-sm'>
                      <Badge variant='primary' outline size='sm'>
                        {formatUtils.dateDuration(
                          thread.callStartedAt,
                          thread.callEndedAt,
                        )}
                      </Badge>
                    </DisplayValue>
                  )}
                </>
              )}

              {thread.category === ThreadCategory.Chat && (
                <DisplayValue label={t`Conversation mode`} className='text-sm'>
                  <Badge
                    size='sm'
                    variant={
                      thread.mode === ThreadMode.AI ? 'secondary' : 'primary'
                    }
                  >
                    {thread.mode === ThreadMode.AI ? 'AI' : 'Human'}
                  </Badge>
                </DisplayValue>
              )}

              <DisplayValue label={t`Message Count`} className='text-sm'>
                {thread.messages?.length || 0}
              </DisplayValue>
            </div>
          </Tabs.Content>

          {thread.category === ThreadCategory.Chat && (
            <Tabs.Content value='context' className='h-full'>
              <ThreadContext thread={thread} />
            </Tabs.Content>
          )}

          <Tabs.Content value='logs' className='h-full'>
            <CallLogs logs={thread.callLogs} />
          </Tabs.Content>

          <Tabs.Content value='costs' className='h-full'>
            <ThreadCosts threadId={thread.id} currency={organizationCurrency} />
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </div>
  );
}
